/**
 * Regras de progressão SIADAP (LGTFP / Decreto Regulamentar n.º 18/2009)
 *
 * Pontos por avaliação:
 *   Excelente   → 3 pontos
 *   Muito Bom   → 2 pontos
 *   Bom         → 1.5 pontos
 *   Regular     → 1 ponto
 *   Inadequado  → 0 pontos
 *
 * Pontos necessários para progressão de nível:
 *   Técnica Superior       → 10 pontos (progressão de 1 nível) / 20 pontos (2 níveis)
 *   Assistente Técnico     → 10 pontos (progressão de 1 nível) / 20 pontos (2 níveis)
 *   Assistente Operacional → 10 pontos (progressão de 1 nível) / 20 pontos (2 níveis)
 *
 * Regra geral: 10 pontos = 1 nível; 20 pontos = 2 níveis (com Excelente em 2 ciclos consecutivos)
 */

export type AvaliacaoSiadap = 'Excelente' | 'Muito Bom' | 'Bom' | 'Regular' | 'Inadequado';
export type Carreira = 'Técnica Superior' | 'Assistente Técnico' | 'Assistente Operacional';

export const PONTOS_POR_AVALIACAO: Record<AvaliacaoSiadap, number> = {
  Excelente: 3,
  'Muito Bom': 2,
  Bom: 1.5,
  Regular: 1,
  Inadequado: 0,
};

/** Pontos necessários para progressão de 1 nível — avaliações 2000–2022 */
export const PONTOS_PARA_PROGRESSAO_PRE2023 = 10;

/** @deprecated use PONTOS_PARA_PROGRESSAO_PRE2023 */
export const PONTOS_PARA_PROGRESSAO_PRE2025 = 10;

/** Pontos necessários para progressão de 1 nível — avaliações 2023–2024 */
export const PONTOS_PARA_PROGRESSAO_2023_2024 = 8;

/** Pontos necessários para progressão de 1 nível — avaliações a partir de 2025 */
export const PONTOS_PARA_PROGRESSAO = 8;

/** Pontos necessários para progressão de 2 níveis (Excelente em 2 ciclos consecutivos) */
export const PONTOS_PARA_PROGRESSAO_2_NIVEIS = 20;

/**
 * Calcula os anos de serviço de um funcionário na função pública.
 * @param dataEntrada - data de entrada na função pública (YYYY-MM-DD)
 * @param anoReferencia - ano de referência para o cálculo (default: ano atual)
 */
export function calcularAnosServico(
  dataEntrada: string | undefined | null,
  anoReferencia: number = new Date().getFullYear()
): number {
  if (!dataEntrada) return 0;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return 0;
  const refDate = new Date(anoReferencia, 11, 31); // 31 de dezembro do ano de referência
  const diffMs = refDate.getTime() - entrada.getTime();
  const diffAnos = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  return Math.floor(diffAnos);
}

/**
 * Regra especial 2024: funcionários com 18+ anos de serviço a 30/08/2023 e 6 pontos têm direito a progressão.
 * Aplica-se apenas ao período 2023–2024.
 * A condição dos 18 anos é verificada SEMPRE à data de 30/08/2023, independentemente do ano de referência.
 * @param dataEntrada - data de entrada na função pública
 * @param pontosAtuais - pontos acumulados do funcionário
 * @param jaProgrediu - true se o funcionário já tem pelo menos uma progressão registada
 * @param anoReferencia - ano de referência (deve ser 2023 ou 2024)
 * @param _limiarNormal - limiar normal do funcionário (mantido para compatibilidade, não afeta a lógica)
 */
export const PONTOS_REGRA_18_ANOS = 6;
export const ANOS_SERVICO_REGRA_ESPECIAL = 18;

/** Data de referência fixa para a regra dos 18 anos: 30/08/2023 */
const DATA_REFERENCIA_18_ANOS = new Date(2023, 7, 30); // mês 7 = agosto (0-indexed)

/**
 * Verifica se o funcionário tinha 18+ anos de serviço a 30/08/2023.
 * Esta data é fixa e não depende do ano de referência da avaliação.
 */
export function temDezoitoAnosEm30Ago2023(
  dataEntrada: string | undefined | null
): boolean {
  if (!dataEntrada) return false;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return false;
  const diffMs = DATA_REFERENCIA_18_ANOS.getTime() - entrada.getTime();
  const diffAnos = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  return Math.floor(diffAnos) >= ANOS_SERVICO_REGRA_ESPECIAL;
}

/**
 * Regra especial 2024: funcionários com 18+ anos de serviço a 30/08/2023 e 6 pontos têm direito a progressão.
 * IMPORTANTE: Esta regra só se aplica UMA VEZ — se o funcionário já progrediu,
 * não pode voltar a usar esta regra, mesmo que ainda tenha 6+ pontos.
 * @param dataEntrada - data de entrada na função pública
 * @param pontosAtuais - pontos acumulados do funcionário
 * @param jaProgrediu - true se o funcionário já tem pelo menos uma progressão registada
 * @param anoReferencia - ano de referência (deve ser 2023 ou 2024)
 * @param _limiarNormal - limiar normal do funcionário (mantido para compatibilidade, não afeta a lógica)
 */
export function aplicaRegraEspecial18Anos(
  dataEntrada: string | undefined | null,
  pontosAtuais: number,
  jaProgrediu: boolean = false,
  anoReferencia: number = 2024,
  _limiarNormal: number = PONTOS_PARA_PROGRESSAO_2023_2024
): boolean {
  if (anoReferencia < 2023 || anoReferencia >= 2025) return false;
  if (jaProgrediu) return false; // Regra dos 18 anos: só uma vez na vida
  // A condição dos 18 anos é sempre verificada à data de 30/08/2023
  return temDezoitoAnosEm30Ago2023(dataEntrada) && pontosAtuais >= PONTOS_REGRA_18_ANOS;
}

/**
 * Devolve os pontos necessários para progressão tendo em conta o ano de referência.
 * 2000–2022 → pontosPre2023 (10 pts por defeito)
 * 2023–2024 → pontos2023_2024 (8 pts por defeito)
 * 2025+     → pontosPos2025 (8 pts por defeito)
 */
export function pontosNecessariosParaProgressao(
  anoReferencia: number,
  pontosPos2025: number = PONTOS_PARA_PROGRESSAO,
  pontosPre2023: number = PONTOS_PARA_PROGRESSAO_PRE2023,
  pontos2023_2024: number = PONTOS_PARA_PROGRESSAO_2023_2024
): number {
  if (anoReferencia < 2023) return pontosPre2023;
  if (anoReferencia < 2025) return pontos2023_2024;
  return pontosPos2025;
}

/**
 * Determina o limiar de progressão de um funcionário com base nos seus registos.
 * 3 períodos:
 *   2000–2022 → pontosPre2023 (10 pts por defeito)
 *   2023–2024 → pontos2023_2024 (8 pts por defeito)
 *   2025+     → pontosPos2025 (8 pts por defeito)
 * Limiar final = o do período mais recente com avaliações.
 */
export function calcularLimiarFuncionario(
  registosSiadap: Array<{ avaliacao: string; periodoAvaliacao?: string }>,
  registosHistorico: Array<{ classificacaoServico: string; ano?: number }>,
  pontosPos2025: number = PONTOS_PARA_PROGRESSAO,
  pontosPre2023: number = PONTOS_PARA_PROGRESSAO_PRE2023,
  pontos2023_2024: number = PONTOS_PARA_PROGRESSAO_2023_2024,
  escalas?: PontosEscalasConfig
): {
  limiar: number;
  periodo: 'pre2023' | 'p2023_2024' | 'pos2025' | 'misto';
  temAvaliacoesPre2025: boolean;
  temAvaliacoesPos2025: boolean;
  pontosPre2025Total: number;
  pontosPos2025Total: number;
} {
  let maxAno = 0;
  let pontosPre = 0;
  let pontosPos = 0;

  registosSiadap.forEach((r) => {
    const ano = anoFinalDoPeriodo(r.periodoAvaliacao ?? '');
    const pts = pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', undefined, escalas);
    if (ano > maxAno) maxAno = ano;
    if (ano < 2025) pontosPre += pts;
    else pontosPos += pts;
  });

  registosHistorico.forEach((r) => {
    const ano = r.ano ?? 0;
    const pts = PONTOS_HISTORICO[r.classificacaoServico] ?? 0;
    if (ano > maxAno) maxAno = ano;
    if (ano < 2025) pontosPre += pts;
    else pontosPos += pts;
  });

  const temPre = pontosPre > 0;
  const temPos = pontosPos > 0;

  let periodo: 'pre2023' | 'p2023_2024' | 'pos2025' | 'misto';
  let limiar: number;

  if (maxAno === 0) {
    // sem avaliações → usa o mais recente
    periodo = 'pos2025';
    limiar = pontosPos2025;
  } else if (maxAno >= 2025) {
    periodo = temPre ? 'misto' : 'pos2025';
    limiar = pontosPos2025;
  } else if (maxAno >= 2023) {
    // Avaliações 2023-2024: se há também avaliações pré-2023, período misto
    // mas o limiar aplicável é sempre o do período mais recente (2023-2024)
    periodo = temPre && pontosPre > 0 ? 'misto' : 'p2023_2024';
    limiar = pontos2023_2024;
  } else {
    // Apenas avaliações pré-2023 → limiar pré-2023 (tipicamente 10 pts)
    periodo = 'pre2023';
    limiar = pontosPre2023;
  }

  return {
    limiar,
    periodo,
    temAvaliacoesPre2025: temPre,
    temAvaliacoesPos2025: temPos,
    pontosPre2025Total: pontosPre,
    pontosPos2025Total: pontosPos,
  };
}

export interface ProgressaoInfo {
  pontosAtuais: number;
  pontosNecessarios: number;
  pontosFaltam: number;
  percentagem: number;
  proximoNivel: number;
  nivelAtual: number;
  status: 'pronto' | 'proximo' | 'em_progresso';
}

/**
 * Calcula os pontos totais de um funcionário a partir dos seus registos SIADAP.
 * @param registos - lista de registos com avaliação
 * @param pontosMap - mapa de pontos por avaliação (opcional, usa defaults se não fornecido)
 */
/** Extrai o ano final de um período como "2023-2024" ou "2021-2022" */
function anoFinalDoPeriodo(periodo: string): number {
  const partes = (periodo ?? '').trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  return isNaN(ano) ? 9999 : ano;
}

/** Pontos para a escala 2000–2022 */
export const PONTOS_ESCALA_PRE2023: Record<string, number> = {
  Excelente: 6,
  Relevante: 4,
  Adequado: 2,
  Inadequado: -2,
  // aliases de compatibilidade (caso existam registos antigos com estes nomes)
  'Muito Bom': 4,
  Bom: 2,
  Regular: 2,
};

/** @deprecated use PONTOS_ESCALA_PRE2023 */
export const PONTOS_ESCALA_ANTIGA = PONTOS_ESCALA_PRE2023;

/** Pontos para a escala 2023–2024 (por defeito igual à antiga, mas configurável) */
export const PONTOS_ESCALA_2023_2024: Record<string, number> = {
  Excelente: 6,
  'Muito Bom': 4,
  Bom: 3,
  Regular: 2,
  Inadequado: 0,
  Relevante: 4,
  Adequado: 2,
};

/** Período de escala aplicável a um funcionário */
export type PeriodoEscala = 'pre2023' | 'p2023_2024' | 'pos2025' | 'misto' | 'sem_registos';

export interface PontosEscalasConfig {
  /** Pontos por avaliação para 2025+ (dinâmico das definições) */
  pos2025?: Partial<Record<string, number>>;
  /** Pontos por avaliação para 2023-2024 (dinâmico das definições) */
  p2023_2024?: Partial<Record<string, number>>;
  /** Pontos por avaliação para pré-2023 (dinâmico das definições) */
  pre2023?: Partial<Record<string, number>>;
}

/** Retorna os pontos de uma avaliação tendo em conta o período e os mapas dinâmicos */
export function pontosParaAvaliacao(
  avaliacao: string,
  periodo: string,
  pontosMap?: Partial<Record<AvaliacaoSiadap, number>>,
  escalas?: PontosEscalasConfig
): number {
  const ano = anoFinalDoPeriodo(periodo);
  if (ano < 2023) {
    const mapa = escalas?.pre2023 ?? PONTOS_ESCALA_PRE2023;
    return mapa[avaliacao] ?? PONTOS_ESCALA_PRE2023[avaliacao] ?? 0;
  }
  if (ano < 2025) {
    const mapa = escalas?.p2023_2024 ?? PONTOS_ESCALA_2023_2024;
    return mapa[avaliacao] ?? PONTOS_ESCALA_2023_2024[avaliacao] ?? 0;
  }
  const mapa = { ...PONTOS_POR_AVALIACAO, ...pontosMap, ...(escalas?.pos2025 ?? {}) };
  return mapa[avaliacao as AvaliacaoSiadap] ?? 0;
}

export function calcularPontosDosRegistos(
  registos: Array<{ avaliacao: string; periodoAvaliacao?: string }>,
  pontosMap?: Partial<Record<AvaliacaoSiadap, number>>,
  escalas?: PontosEscalasConfig
): number {
  return registos.reduce((total, r) => {
    return total + pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '9999', pontosMap, escalas);
  }, 0);
}

/**
 * Calcula os pontos líquidos do ciclo atual de um funcionário.
 * Fórmula: max(0, pontosSiadap + pontosHistorico + pontosManuais - pontosDescontados)
 * Os pontos manuais (pontosAtuaisFallback) são SEMPRE somados aos pontos dos registos.
 */
/**
 * Pontos para avaliações do histórico manual (escala antiga, sem período).
 * Relevante = 4, Bom = 3, Adequado = 2, Excelente = 3, Muito Bom = 2
 */
export const PONTOS_HISTORICO: Record<string, number> = {
  Excelente: 6,
  Relevante: 4,
  Adequado: 2,
  Inadequado: -2,
  // aliases de compatibilidade
  'Muito Bom': 4,
  Bom: 2,
  Regular: 2,
};

export function calcularPontosCiclo(
  registosSiadap: Array<{ avaliacao: string; periodoAvaliacao?: string }>,
  registosHistorico: Array<{ classificacaoServico: string }>,
  pontosDescontados: number,
  pontosManuais: number,
  pontosMap?: Partial<Record<AvaliacaoSiadap, number>>,
  _avaliacaoPontosExtra?: Record<string, number>,
  escalas?: PontosEscalasConfig
): number {
  // SIADAP: respeita o período (escala antiga vs nova) com escalas dinâmicas
  const pontosSiadap = calcularPontosDosRegistos(registosSiadap, pontosMap, escalas);
  // Histórico manual: usa sempre a escala do histórico (Relevante=4, Bom=3, Adequado=2)
  const pontosHistorico = registosHistorico.reduce((sum, r) => {
    return sum + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0);
  }, 0);
  return Math.max(0, pontosSiadap + pontosHistorico + pontosManuais - pontosDescontados);
}

/**
 * Calcula o indicador de progressão de nível para um funcionário.
 * @param pontosAtuais - pontos acumulados do funcionário
 * @param nivelAtual - nível atual do funcionário
 * @param pontosParaProgressao - pontos necessários (default: PONTOS_PARA_PROGRESSAO)
 */
export function calcularProgressao(
  pontosAtuais: number,
  nivelAtual: number,
  pontosParaProgressao: number = PONTOS_PARA_PROGRESSAO
): ProgressaoInfo {
  const pontosFaltam = Math.max(0, pontosParaProgressao - pontosAtuais);
  const percentagem = Math.min(100, (pontosAtuais / pontosParaProgressao) * 100);
  const proximoNivel = nivelAtual + 1;

  let status: ProgressaoInfo['status'] = 'em_progresso';
  if (pontosAtuais >= pontosParaProgressao) {
    status = 'pronto';
  } else if (percentagem >= 70) {
    status = 'proximo';
  }

  return {
    pontosAtuais,
    pontosNecessarios: pontosParaProgressao,
    pontosFaltam,
    percentagem,
    proximoNivel,
    nivelAtual,
    status,
  };
}

export function getStatusColor(status: ProgressaoInfo['status']): {
  bar: string;
  text: string;
  bg: string;
  badge: string;
} {
  switch (status) {
    case 'pronto':
      return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' };
    case 'proximo':
      return { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' };
    default:
      return { bar: 'bg-slate-300', text: 'text-slate-500', bg: 'bg-slate-50', badge: 'bg-slate-100 text-slate-600' };
  }
}
